import type { Terminal } from '@xterm/xterm';
import { CLEAR_PROMPT, CTRL_C, CTRL_C_EXIT_CODE } from './constants';
import { BOLT_BOOTSTRAP_ID } from '~/lib/constants';
import { BaseShellAction, type BoltAction } from '~/lib/runtime/actions';
import { workbenchStore } from '~/lib/stores/workbench';
import { withResolvers } from '~/utils/promises';
import type { Shell } from '~/utils/shell';

export interface Command {
  exitCode?: number;
  shellAction: Extract<BoltAction, BaseShellAction>;
  abortController: AbortController;
  skip(): void;
  onExit: (exitCode: number) => void;
}

interface WriteSessionOptions {
  /**
   * Whether output should be accumulated in a buffer or not
   * for output listeners.
   */
  useBufferedOutput?: boolean;
}

type ProcessStartedListener = (pid: number) => void;
type ProcessEndedListener = (pid: number, exitCode: number) => void;
type OutputListener = (output: string) => void;

export type ShellId = 'bolt' | 'deploy';

/**
 * The ShellDriver can drive a shell fully automatically. You can just feed it commands, and the ShellDriver
 * will execute them in a real shell.
 */
export class ShellDriver {
  private _commands: Command[] = [];
  private _shell?: Shell;
  private _terminal?: Terminal;
  private _restarting = false;

  private _writeSession: Promise<void> | undefined;

  private _currentCommand?: Command;
  private _processList = new Set<number>();
  private _killing: Promise<void> | undefined;

  private _oldMessageIDs = new Set<string>();

  private _promptListeners = new Set<() => void>();
  private _processStartedListeners = new Set<ProcessStartedListener>();
  private _processEndedListeners = new Set<ProcessEndedListener>();
  private _outputListeners = new Set<OutputListener>();

  start(shell: Shell, terminal: Terminal) {
    if (this._shell) {
      throw new Error('It is not possible to start the ShellDriver twice.');
    }

    this._shell = shell;
    this._terminal = terminal;

    let ignoreNewProcesses = false;

    shell.registerOscHandler((data: string) => {
      if (data === 'prompt') {
        /**
         * If we see a new prompt before all processes exitted, we ignore all new processes from this point on. It's
         * very likely that JSH was started inside JSH. It should not really happen, unless the user literally starts
         * `jsh --osc`.
         */
        ignoreNewProcesses = this._processList.size > 0;

        if (ignoreNewProcesses) {
          return;
        }

        // the prompt is rendered and can receive data
        for (const listener of this._promptListeners) {
          listener();
        }
      } else if (data.startsWith('pid=')) {
        if (ignoreNewProcesses) {
          // we are likely running JSH in JSH, so ignore all new processes
          return;
        }

        const pid = Number.parseInt(data.slice('pid='.length), 10);

        this._processList.add(pid);

        for (const listener of this._processStartedListeners) {
          listener(pid);
        }
      } else if (data.startsWith('exit=')) {
        // a process exited
        const [pid, exitCode] = data
          .slice('exit='.length)
          .split(':')
          .map((input) => Number.parseInt(input));

        /**
         * If the `pid` is `0`, it means the command was not found or some other shell error happened. In that case,
         * we always just want to notify the listeners.
         */
        if (pid !== 0 && !this._processList.has(pid)) {
          return;
        }

        // delete the pid from the list
        this._processList.delete(pid);

        for (const listener of this._processEndedListeners) {
          listener(pid, exitCode);
        }
      }
    });

    shell.registerOutputHandler((data) => {
      this._outputListeners.forEach((listener) => {
        listener(data);
      });
    });

    this._startProcessing();
  }

  getCurrentCommand() {
    return this._currentCommand;
  }

  addOutputListener(fn: (output: string) => void) {
    this._outputListeners.add(fn);
  }

  private _shouldKillRunningCommandFor({ shellAction }: Command) {
    const currentShellAction = this._currentCommand?.shellAction;

    if (!currentShellAction || shellAction.messageId === currentShellAction.messageId) {
      return false;
    }

    if (currentShellAction.messageId === BOLT_BOOTSTRAP_ID && currentShellAction.type === 'install') {
      // we never kill the install action from the bootstrap
      return false;
    }

    if (currentShellAction.type === 'start' && shellAction.type === 'start') {
      const hasSameCommandExecuting = currentShellAction.command === shellAction.command;

      if (hasSameCommandExecuting || shellAction.autocreated) {
        return false;
      }

      const hasSameCommandQueued = this._commands.some(
        (pendingCommand) => pendingCommand.shellAction.command === shellAction.command,
      );

      if (hasSameCommandQueued) {
        return false;
      }
    }

    if (currentShellAction.type !== 'start' && shellAction.messageId === BOLT_BOOTSTRAP_ID) {
      /**
       * If the current shell action is not a start action and the incoming shell action is the bootstrap action,
       * we do not want to kill the command because it is part of a streaming response which is faster than the
       * bootstrap was finished. This is likely to happen for cached AI stream responses.
       */
      return false;
    }

    return true;
  }

  processCommand(command: Command) {
    if (this._oldMessageIDs.has(command.shellAction.messageId)) {
      // if we receive a command which has an old message ID, we mark it as skipped
      command.skip();

      return;
    }

    if (this._currentCommand && this._shouldKillRunningCommandFor(command)) {
      // keep track of all the old message IDs so that if new commands come in with one of those we can skip them
      this._oldMessageIDs.add(this._currentCommand.shellAction.messageId);

      /**
       * If we are adding a new command which has a different messageId than the currently running command,
       * we have to filter out all commands with a different message id and mark them as skipped.
       */
      const previousCommands = this._commands;

      this._commands = [];

      for (const previousCommand of previousCommands) {
        if (previousCommand.shellAction.messageId === command.shellAction.messageId) {
          this._commands.push(previousCommand);
        } else {
          previousCommand.skip();
        }
      }

      // kill the current command
      this._killing = this._kill();
    }

    if (!this._currentCommand && this._processList.size > 0) {
      // there's no command running that was provided to the driver, so it's something the user started, kill it
      this._killing = this._kill();
    }

    this._commands.push(command);

    this._startProcessing();
  }

  restartCurrentCommand() {
    if (!this._currentCommand) {
      return;
    }

    const framework = workbenchStore.framework.get();

    if (framework?.name === 'expo') {
      return;
    }

    this._restarting = true;

    const command = this._currentCommand;

    this._commands.unshift(command);

    this._killing = this._kill();
  }

  createWriteSession({ useBufferedOutput }: WriteSessionOptions = {}) {
    if (!this._terminal) {
      return undefined;
    }

    const terminal = this._terminal;

    if (this._writeSession) {
      throw new Error('A write session is already active');
    }

    const { promise, resolve } = withResolvers<void>();

    this._writeSession = promise;
    terminal.options.disableStdin = true;

    let bufferedOutput = '';

    return {
      killExistingProcesses: () => {
        if (this._processList.size > 0) {
          return this._kill();
        }

        return Promise.resolve();
      },
      writeShell: (input: string) => {
        this._shell!.write(input);
      },
      write: (message: string) => {
        terminal.write(message, () => {
          terminal.scrollToBottom();
        });

        if (!useBufferedOutput) {
          this._outputListeners.forEach((listener) => {
            listener(message);
          });
        } else {
          bufferedOutput += message;
        }
      },
      release: () => {
        resolve();

        if (useBufferedOutput) {
          this._outputListeners.forEach((listener) => {
            listener(bufferedOutput);
          });
        }

        terminal.options.disableStdin = false;

        this._writeSession = undefined;
      },
    };
  }

  private async _startProcessing() {
    if (!this._shell) {
      return;
    }

    if (this._killing) {
      const killing = this._killing;

      this._killing = undefined;

      await killing;

      this._currentCommand = undefined;
    }

    if (this._currentCommand) {
      // we exit if we are already running a command
      return;
    }

    const command = this._commands.shift();

    if (!command) {
      return;
    }

    // make sure the shell is ready
    await this._shell.ready();

    // make sure there are no write session active
    await this._writeSession;

    this._currentCommand = command;

    const exitCode = await this._runCommand(command.shellAction.command, (output) => {
      command.shellAction.output += output;
    });

    /**
     * Don't run the autocreated start commands from the next artifacts
     * when the user exits a previous start command by inputting Ctrl+C.
     */
    if (exitCode === CTRL_C_EXIT_CODE && command.shellAction.type === 'start') {
      const commands = [];

      for (const command of this._commands) {
        if (command.shellAction.type === 'start' && command.shellAction.autocreated && !this._restarting) {
          command.skip();
        } else {
          commands.push(command);
        }
      }

      this._commands = commands;
    }

    command.exitCode = exitCode;

    this._currentCommand = undefined;
    this._restarting = false;

    command.onExit(exitCode);

    this._startProcessing();
  }

  private async _kill() {
    return new Promise<void>((resolve) => {
      const timeout = setTimeout(async () => {
        /**
         * We notify all current process exit listeners with an exit code of 130 which is usually the exit code for
         * a process which got killed with SIGINT, unless the process captures SIGINT and exits differently. Given
         * the process does not gracefully exit after sending a SIGINT, we can just use 130 here.
         */
        for (const pid of this._processList) {
          for (const listener of this._processEndedListeners) {
            listener(pid, 130);
          }
        }

        this._processList.clear();

        // hard restart the shell if we couldn't gracefully exit the process
        await this._shell!.restart(false);

        resolve();
      }, 200);

      const listener = () => {
        clearTimeout(timeout);

        this._promptListeners.delete(listener);

        resolve();
      };

      this._promptListeners.add(listener);

      // we try to gracefully kill the current process by sending Ctrl+C
      this._shell!.write(CTRL_C);
    });
  }

  private _runCommand(command: string, outputListener: (data: string) => void) {
    return new Promise<number>((resolve) => {
      let commandExitCode = 0;
      let isAfterInputCommand = false;

      const handleOutput = (output: string) => {
        if (isAfterInputCommand) {
          outputListener(output);
        }

        /**
         * It's not ideal, but we check if we see the end bracketed paste mode control character. This is the point
         * where a command will be executed and we can start listening for output.
         */
        isAfterInputCommand ||= output.includes('\u001B[?2004l');
      };

      const exitListener = (_pid: number, exitCode: number) => {
        this._outputListeners.delete(handleOutput);
        commandExitCode = exitCode;

        if (this._processList.size === 0) {
          this._outputListeners.delete(outputListener);
        }
      };

      const promptListener = () => {
        this._outputListeners.delete(handleOutput);
        this._processEndedListeners.delete(exitListener);
        this._promptListeners.delete(promptListener);

        resolve(commandExitCode);
      };

      this._promptListeners.add(promptListener);
      this._processEndedListeners.add(exitListener);

      this._shell!.write(`${CLEAR_PROMPT}${command}\n`);

      this._outputListeners.add(handleOutput);
    });
  }
}
